import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";

// Spritz pivot index: roughly 30-40% into the word
function getPivotIndex(word) {
  const clean = word.replace(/[^a-zA-Z0-9]/g, "");
  const len = clean.length;
  if (len <= 1) return 0;
  if (len <= 5) return 1;
  if (len <= 9) return 2;
  if (len <= 13) return 3;
  return 4;
}

// Common function words that carry little meaning and can flash by faster.
const FUNCTION_WORDS = new Set([
  "a","an","the","of","in","on","at","to","by","or","and","but","for",
  "as","is","it","be","if","we","he","she","am","are","was","its","our",
  "my","no","so","do","up","nor","yet","his","her","i",
]);

// How long to linger on a word relative to the base WPM delay.
function getDelayMultiplier(word, isLastInParagraph) {
  // Structural break — long pause between title/author/body sections
  if (isLastInParagraph) return 3.5;

  // Ellipsis — longest punctuation pause (trailing off / unfinished thought)
  if (/[.…]{3,}['")\]]*$/.test(word) || /…['")\]]*$/.test(word)) return 2.5;

  // Sentence end — pause so the brain registers the full stop
  if (/[.!?]['")\]]*$/.test(word)) return 2.2;

  // Em-dash mid-sentence structural break
  if (/—$/.test(word)) return 1.9;

  // Clause boundary — colon gets slightly longer than comma/semicolon
  if (/:['")\]]*$/.test(word)) return 1.7;
  if (/[,;]['")\]]*$/.test(word)) return 1.5;

  // Numbers and decimals take longer to parse
  if (/^\d[\d,._%-]*$/.test(word)) return 1.3;

  // Function words carry little content — show them quickly
  const bare = word.toLowerCase().replace(/[^a-z]/g, "");
  if (FUNCTION_WORDS.has(bare)) return 0.6;

  // Hyphenated compounds — each segment adds extra parsing time.
  // Trailing hyphen = PDF line-break artifact: reader must mentally join the
  // next word, so give it extra time regardless of part count.
  if (/-/.test(word)) {
    const parts = word.split("-").filter(Boolean);
    const base = word.endsWith("-") ? 4.5 : 2.5;
    return Math.min(base + (parts.length - 1) * 0.5, 5.0);
  }

  // Word-length scaling: long words need more display time.
  // Each letter beyond 8 chars adds ~5% extra time, capped at +50%.
  const clean = word.replace(/[^a-zA-Z0-9]/g, "");
  if (clean.length > 8) return Math.min(1.0 + (clean.length - 8) * 0.05, 1.5);

  return 1.0;
}

// Split text into word objects, preserving paragraph-break information.
// Double newlines in the source text mark structural boundaries (title,
// author, chapter headings, etc.) and get a longer pause after the last
// word of each section.
function splitTextWithPauses(text) {
  if (!text) return [];
  const paragraphs = text.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);
  const result = [];
  paragraphs.forEach((para, pIdx) => {
    const words = para.split(/\s+/).filter(Boolean);
    words.forEach((word, wIdx) => {
      const isLastInPara = wIdx === words.length - 1 && pIdx < paragraphs.length - 1;
      result.push({ word, multiplier: getDelayMultiplier(word, isLastInPara) });
    });
  });
  return result;
}

// Returns the index of the first word of the sentence containing idx.
// Scans backwards for a sentence-ending word; if none found returns 0.
function findSentenceStart(words, idx) {
  for (let i = idx - 1; i >= 0; i--) {
    if (/[.!?]['")\]]*$/.test(words[i].word)) return i + 1;
  }
  return 0;
}

// Returns the index of the first word of the next sentence after idx.
function findNextSentenceStart(words, idx) {
  for (let i = idx; i < words.length - 1; i++) {
    if (/[.!?]['")\]]*$/.test(words[i].word)) return i + 1;
  }
  return words.length - 1;
}

export default function SpeedReader({ text, startIndex = 0, onIndexChange, onPause, onAddCitation }) {
  const words = useMemo(() => splitTextWithPauses(text), [text]);
  const [currentIndex, setCurrentIndex] = useState(startIndex);
  const [playing, setPlaying] = useState(false);
  const [wpm, setWpm] = useState(300);
  const [citationDraft, setCitationDraft] = useState("");

  const timeoutRef = useRef(null);
  const citationInputRef = useRef(null);
  // Refs so the timeout loop always reads the latest values without
  // needing to be recreated every time they change.
  const playingRef = useRef(false);
  const wpmRef = useRef(wpm);
  const currentIndexRef = useRef(currentIndex);

  useEffect(() => { wpmRef.current = wpm; }, [wpm]);
  useEffect(() => { currentIndexRef.current = currentIndex; }, [currentIndex]);

  // Fire onPause when transitioning playing → paused
  const prevPlayingRef = useRef(false);
  useEffect(() => {
    if (prevPlayingRef.current && !playing) {
      onPause?.(currentIndex, words[currentIndex]?.word || "");
    }
    prevPlayingRef.current = playing;
  }, [playing]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset when text changes
  useEffect(() => {
    setPlaying(false);
    setCurrentIndex(startIndex < words.length ? startIndex : 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [words]);

  // Seek when startIndex jumps externally (e.g. PDF click).
  // During normal playback startIndex mirrors currentIndex (diff ≈ 0-1).
  // A PDF click causes a large jump, so > 5 reliably distinguishes the two.
  useEffect(() => {
    if (Math.abs(startIndex - currentIndexRef.current) > 5) {
      setPlaying(false);
      currentIndexRef.current = startIndex;
      setCurrentIndex(startIndex);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startIndex]);

  // Notify parent of index changes
  useEffect(() => {
    onIndexChange?.(currentIndex);
  }, [currentIndex, onIndexChange]);

  // Recursive timeout loop — replaces setInterval so each word can have
  // its own delay multiplier for smart pacing.
  useEffect(() => {
    playingRef.current = playing;
    if (!playing || words.length === 0) {
      clearTimeout(timeoutRef.current);
      return;
    }

    const advance = () => {
      if (!playingRef.current) return;
      const idx = currentIndexRef.current;
      if (idx >= words.length - 1) {
        setPlaying(false);
        return;
      }
      const baseDelay = Math.round(60000 / wpmRef.current);
      const multiplier = words[idx]?.multiplier ?? 1;
      // Paragraph breaks can go up to 900ms (clearly a beat, not a freeze).
      // All other pauses cap at 600ms so the reader never feels stalled.
      const hardCap = multiplier >= 4 ? 2000 : multiplier >= 1.8 ? 1200 : 600;
      const delay = Math.min(Math.round(baseDelay * multiplier), hardCap);
      timeoutRef.current = setTimeout(() => {
        setCurrentIndex((i) => {
          const next = i + 1;
          currentIndexRef.current = next;
          return next;
        });
        advance();
      }, delay);
    };

    advance();
    return () => {
      playingRef.current = false;
      clearTimeout(timeoutRef.current);
    };
  }, [playing, words]); // wpm changes handled via wpmRef — no restart needed

  const togglePlay = useCallback(() => {
    if (currentIndex >= words.length - 1) setCurrentIndex(0);
    setPlaying((p) => !p);
  }, [currentIndex, words.length]);

  const skipBack = useCallback(() => {
    clearTimeout(timeoutRef.current);
    const wasPlaying = playingRef.current;
    playingRef.current = false;
    setPlaying(false);
    const newIdx = Math.max(0, currentIndexRef.current - 10);
    currentIndexRef.current = newIdx;
    setCurrentIndex(newIdx);
    if (!wasPlaying) onPause?.(newIdx, words[newIdx]?.word || "");
  }, [words, onPause]);

  const skipForward = useCallback(() => {
    clearTimeout(timeoutRef.current);
    const wasPlaying = playingRef.current;
    playingRef.current = false;
    setPlaying(false);
    const newIdx = Math.min(words.length - 1, currentIndexRef.current + 10);
    currentIndexRef.current = newIdx;
    setCurrentIndex(newIdx);
    if (!wasPlaying) onPause?.(newIdx, words[newIdx]?.word || "");
  }, [words, onPause]);

  const restart = useCallback(() => {
    clearTimeout(timeoutRef.current);
    const wasPlaying = playingRef.current;
    playingRef.current = false;
    setPlaying(false);
    currentIndexRef.current = 0;
    setCurrentIndex(0);
    if (!wasPlaying) onPause?.(0, words[0]?.word || "");
  }, [words, onPause]);

  const skipToPrevSentence = useCallback(() => {
    clearTimeout(timeoutRef.current);
    playingRef.current = false;
    setPlaying(false);
    const cur = currentIndexRef.current;
    const curStart = findSentenceStart(words, cur);
    // If we're already at/near the sentence start, jump to the previous sentence
    const targetIdx = cur <= curStart + 1
      ? findSentenceStart(words, Math.max(0, curStart - 1))
      : curStart;
    currentIndexRef.current = targetIdx;
    setCurrentIndex(targetIdx);
  }, [words]);

  const skipToNextSentence = useCallback(() => {
    clearTimeout(timeoutRef.current);
    playingRef.current = false;
    setPlaying(false);
    const nextIdx = findNextSentenceStart(words, currentIndexRef.current);
    currentIndexRef.current = nextIdx;
    setCurrentIndex(nextIdx);
  }, [words]);

  // Keyboard shortcuts:
  //   Space       — play/pause
  //   [ / ]       — WPM −25 / +25
  //   ArrowLeft   — previous sentence
  //   ArrowRight  — next sentence
  useEffect(() => {
    const handler = (e) => {
      if (e.target !== document.body) return;
      if (e.code === "Space") {
        e.preventDefault();
        setPlaying((p) => !p);
      } else if (e.code === "BracketLeft") {
        e.preventDefault();
        setWpm((w) => Math.max(100, w - 25));
      } else if (e.code === "BracketRight") {
        e.preventDefault();
        setWpm((w) => Math.min(600, w + 25));
      } else if (e.code === "ArrowLeft") {
        e.preventDefault();
        skipToPrevSentence();
      } else if (e.code === "ArrowRight") {
        e.preventDefault();
        skipToNextSentence();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [skipToPrevSentence, skipToNextSentence]);

  // Seek bar — click or drag to jump to any position
  const seekBarRef = useRef(null);

  const seekTo = useCallback((clientX) => {
    if (!seekBarRef.current || words.length === 0) return;
    const { left, width } = seekBarRef.current.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (clientX - left) / width));
    const idx = Math.round(pct * (words.length - 1));
    currentIndexRef.current = idx;
    setCurrentIndex(idx);
  }, [words.length]);

  const handleSeekMouseDown = useCallback((e) => {
    e.preventDefault();
    const wasPlaying = playingRef.current;
    setPlaying(false);
    seekTo(e.clientX);
    const onMove = (ev) => seekTo(ev.clientX);
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      // If the reader was already paused before seeking, the playing→paused
      // transition won't fire. Fire onPause explicitly so the PDF syncs.
      if (!wasPlaying) {
        const idx = currentIndexRef.current;
        onPause?.(idx, words[idx]?.word || "");
      }
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }, [seekTo, words, onPause]);

  const handleSeekTouchStart = useCallback((e) => {
    e.preventDefault();
    const wasPlaying = playingRef.current;
    setPlaying(false);
    seekTo(e.touches[0].clientX);
    const onMove = (ev) => seekTo(ev.touches[0].clientX);
    const onEnd = () => {
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onEnd);
      if (!wasPlaying) {
        const idx = currentIndexRef.current;
        onPause?.(idx, words[idx]?.word || "");
      }
    };
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", onEnd);
  }, [seekTo, words, onPause]);

  const handleAddCitation = useCallback(() => {
    if (!citationDraft.trim() || !onAddCitation) return;
    const wordContext = words
      .slice(Math.max(0, currentIndex - 4), currentIndex + 5)
      .map((w) => w.word)
      .join(" ");
    onAddCitation({ text: citationDraft.trim(), wordIndex: currentIndex, wordContext });
    setCitationDraft("");
    // Dismiss mobile keyboard after saving
    citationInputRef.current?.blur();
  }, [citationDraft, currentIndex, words, onAddCitation]);

  if (!text || words.length === 0) {
    return (
      <div className="speed-reader speed-reader-empty">
        <p className="speed-reader-hint">Upload a PDF to start reading</p>
      </div>
    );
  }

  const currentWord = words[currentIndex]?.word || "";
  const currentMultiplier = words[currentIndex]?.multiplier ?? 1;
  const pivotIdx = getPivotIndex(currentWord);
  const before = currentWord.slice(0, pivotIdx);
  const pivot = currentWord[pivotIdx] || "";
  const after = currentWord.slice(pivotIdx + 1);

  // Scale font down for long after-parts so they never clip, and for any
  // hyphenated word so right-side letters stay physically close to the pivot.
  // Focal box is min(100%, 92vw); half-width varies by viewport.
  // constant = (halfWidthPx / 16px/rem) / 0.65avgCharWidth
  // Desktop: focal max-width 560px → half 280px → constant ≈ 26.9 (cap at 25 for safety)
  // Mobile: focal = 92vw → at 375px, half ≈ 172px → constant ≈ 16.5
  const focalConstant = Math.min(window.innerWidth * 0.46, 280) / 16 / 0.65;
  const isHyphenated = currentWord.includes("-");
  const dynamicWordStyle =
    after.length >= 6
      ? { fontSize: `${Math.max(1.5, Math.min(4.5, focalConstant / after.length)).toFixed(2)}rem` }
      : isHyphenated
      ? { fontSize: `${Math.min(3, focalConstant / 4).toFixed(2)}rem` }
      : undefined;
  const progress = words.length > 1 ? currentIndex / (words.length - 1) : 0;
  const pct = Math.round(progress * 100);

  // Time remaining estimate
  const wordsLeft = words.length - 1 - currentIndex;
  const secsLeft = Math.round((wordsLeft / wpm) * 60);
  const timeLeft = secsLeft >= 60
    ? `${Math.ceil(secsLeft / 60)}m left`
    : `${secsLeft}s left`;

  // Context window shown when paused — words surrounding the current position
  const CONTEXT_BEFORE = 8;
  const CONTEXT_AFTER = 8;
  const contextStart = Math.max(0, currentIndex - CONTEXT_BEFORE);
  const contextEnd = Math.min(words.length, currentIndex + CONTEXT_AFTER + 1);
  const contextWords = words.slice(contextStart, contextEnd);

  return (
    <div className="speed-reader">
      {/* Focal word display */}
      <div
        className={`speed-reader-focal${currentMultiplier >= 1.8 ? " speed-reader-focal--pause" : ""}`}
        aria-live="polite"
        aria-atomic="true"
      >
        <div className="speed-reader-word" key={currentIndex} style={dynamicWordStyle}>
          <span className="speed-reader-before">{before}</span>
          <span className="speed-reader-pivot">{pivot}</span>
          <span className="speed-reader-after">{after}</span>
        </div>
      </div>

      {/* Context strip — shown when paused so you know where you are */}
      {!playing && (
        <div className="speed-reader-context" aria-label="Reading context">
          {contextWords.map((w, i) => {
            const absoluteIdx = contextStart + i;
            return (
              <span
                key={absoluteIdx}
                className={
                  absoluteIdx === currentIndex
                    ? "speed-reader-context-current"
                    : "speed-reader-context-word"
                }
              >
                {w.word}{" "}
              </span>
            );
          })}
        </div>
      )}

      {/* Progress */}
      <div className="speed-reader-progress-row">
        <span className="speed-reader-counter">
          {currentIndex + 1} / {words.length}
        </span>
        <div
          className="speed-reader-progress-bar"
          title={`${pct}% — click or drag to seek`}
          ref={seekBarRef}
          onMouseDown={handleSeekMouseDown}
          onTouchStart={handleSeekTouchStart}
          style={{ cursor: "pointer" }}
        >
          <div className="speed-reader-progress-fill" style={{ width: `${pct}%` }} />
        </div>
        <span className="speed-reader-pct">{timeLeft}</span>
      </div>

      {/* Playback controls */}
      <div className="speed-reader-controls">
        <button type="button" className="speed-reader-btn" onClick={restart}
          title="Restart from beginning" aria-label="Restart">↩</button>
        <button type="button" className="speed-reader-btn" onClick={skipToPrevSentence}
          title="Previous sentence (←)" aria-label="Previous sentence">«</button>
        <button type="button" className="speed-reader-btn" onClick={skipBack}
          title="Back 10 words" aria-label="Skip back 10 words">−10</button>
        <button type="button" className="speed-reader-btn speed-reader-btn-play"
          onClick={togglePlay} aria-label={playing ? "Pause" : "Play"} title="Space to toggle">
          {playing ? "⏸" : "▶"}
        </button>
        <button type="button" className="speed-reader-btn" onClick={skipForward}
          title="Forward 10 words" aria-label="Skip forward 10 words">+10</button>
        <button type="button" className="speed-reader-btn" onClick={skipToNextSentence}
          title="Next sentence (→)" aria-label="Next sentence">»</button>
      </div>

      {/* WPM speed control */}
      <div className="speed-reader-speed-row">
        <span className="speed-reader-speed-label">{wpm} WPM</span>
        <input
          id="wpm-slider"
          type="range"
          min="100"
          max="600"
          step="25"
          value={wpm}
          onChange={(e) => setWpm(Number(e.target.value))}
          className="speed-reader-slider"
          aria-label="Reading speed in words per minute"
        />
        <span className="speed-reader-speed-hint">
          {wpm <= 150 ? "slow" : wpm <= 300 ? "normal" : wpm <= 450 ? "fast" : "very fast"}
        </span>
      </div>

      {/* Citation input — only visible when paused */}
      {!playing && onAddCitation && (
        <div className="speed-reader-citation-row">
          <input
            ref={citationInputRef}
            type="text"
            className="speed-reader-citation-input"
            value={citationDraft}
            onChange={(e) => setCitationDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleAddCitation();
              }
            }}
            placeholder="Note at this position… (Enter to save)"
            aria-label="Add a reading note at current position"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
          />
          <button
            type="button"
            className="speed-reader-btn speed-reader-citation-btn"
            onClick={handleAddCitation}
            disabled={!citationDraft.trim()}
            aria-label="Save note"
            title="Save note"
          >
            +
          </button>
        </div>
      )}
    </div>
  );
}
